import { useEffect, useState } from "react";
import { MessageSquare, Phone, Send, User } from "lucide-react";
import { Sheet, SheetContent, SheetDescription, SheetTitle } from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { useEndAdminChat, useLead, useLeadMessages, useSendAdminMessage, useStartAdminChat } from "@/lib/queries";
import { formatDate, formatRelative } from "@/lib/utils";
import { useToast } from "@/components/ui/toast";
import type { Lead, Message } from "@/lib/types";

export function LeadDrawer({ lead, onClose }: { lead: Lead | null; onClose: () => void }) {
  const open = lead !== null;
  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
      <SheetContent
        side="right"
        className="w-full max-w-2xl overflow-y-auto scrollbar-thin sm:max-w-2xl"
      >
        {lead && <LeadDrawerBody leadId={lead.id} />}
      </SheetContent>
    </Sheet>
  );
}

function LeadDrawerBody({ leadId }: { leadId: string }) {
  const { data: lead, isLoading } = useLead(leadId);
  const { data: messages } = useLeadMessages(leadId);
  const [tab, setTab] = useState<"profile" | "conversation">("profile");

  if (isLoading || !lead) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-6 w-48" />
        <Skeleton className="h-4 w-72" />
        <Skeleton className="h-32 w-full" />
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col">
      <div className="pb-4">
        <SheetTitle>{lead.full_name || "Unnamed lead"}</SheetTitle>
        <SheetDescription className="mt-1 flex flex-wrap items-center gap-2">
          {lead.phone && (
            <span className="inline-flex items-center gap-1">
              <Phone className="h-3 w-3" /> <span className="font-mono">{lead.phone}</span>
            </span>
          )}
          {lead.telegram_username && (
            <a
              href={`https://t.me/${lead.telegram_username}`}
              target="_blank"
              rel="noreferrer"
              className="text-accent underline-offset-4 hover:underline"
            >
              @{lead.telegram_username}
            </a>
          )}
          {lead.alfacrm_lead_id ? (
            <Badge tone="outline" className="font-mono">CRM #{lead.alfacrm_lead_id}</Badge>
          ) : (
            <Badge tone="warning">no CRM lead</Badge>
          )}
        </SheetDescription>
      </div>

      <div className="flex border-b border-line">
        <TabButton active={tab === "profile"} onClick={() => setTab("profile")}>
          <User className="h-4 w-4" /> Profile
        </TabButton>
        <TabButton active={tab === "conversation"} onClick={() => setTab("conversation")}>
          <MessageSquare className="h-4 w-4" /> Conversation
          {messages && <span className="ml-1 text-xs text-ink-faint">({messages.length})</span>}
        </TabButton>
      </div>

      <div className="flex-1 overflow-y-auto py-4 scrollbar-thin">
        {tab === "profile" ? <LeadProfile lead={lead} /> : <LeadConversation leadId={leadId} messages={messages} />}
      </div>
    </div>
  );
}

function TabButton({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`-mb-px inline-flex items-center gap-1.5 border-b-2 px-3 py-2 text-sm transition-colors ${
        active ? "border-accent text-ink font-medium" : "border-transparent text-ink-muted hover:text-ink"
      }`}
    >
      {children}
    </button>
  );
}

function LeadProfile({ lead }: { lead: Lead }) {
  const sections: Array<{ heading: string; fields: Array<[string, React.ReactNode]> }> = [
    {
      heading: "Program",
      fields: [
        ["Program", lead.program],
        ["Subjects", lead.subjects?.join(", ")],
        ["Mode", lead.learning_mode],
        ["Branch", lead.preferred_branch],
        ["Grade", lead.grade],
      ],
    },
    {
      heading: "Goals",
      fields: [
        ["Target score", lead.target_score],
        ["Deadline", lead.deadline],
        ["Destination", lead.destination],
      ],
    },
    {
      heading: "Placement",
      fields: [
        ["Math level", lead.math_level],
        ["English level", lead.english_level],
        ["SAT total", lead.sat_score],
        ["SAT math", lead.sat_math_score],
        ["SAT english", lead.sat_english_score],
        ["IELTS", lead.ielts_score],
        ["CEFR", lead.cefr_level],
        ["Milliy", lead.milliy_cert_level],
        ["Certificate proof", lead.certificate_proof_status],
      ],
    },
    {
      heading: "Selection",
      fields: [
        ["Selected course", lead.selected_course_id],
        ["Selected group", lead.selected_group_id],
      ],
    },
    {
      heading: "System",
      fields: [
        ["Created", formatDate(lead.created_at)],
        ["Updated", formatDate(lead.updated_at)],
        ["Telegram ID", lead.telegram_user_id],
      ],
    },
  ];

  return (
    <div className="space-y-6">
      {sections.map((section) => {
        const present = section.fields.filter(([, value]) => value !== null && value !== "" && value !== undefined);
        if (present.length === 0) return null;
        return (
          <section key={section.heading}>
            <h4 className="mb-2 text-xs font-medium uppercase tracking-wider text-ink-faint">
              {section.heading}
            </h4>
            <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
              {present.map(([label, value]) => (
                <div key={label} className="contents">
                  <dt className="text-ink-muted">{label}</dt>
                  <dd className="font-medium text-ink truncate">{value}</dd>
                </div>
              ))}
            </dl>
          </section>
        );
      })}

      {lead.notes && (
        <section>
          <h4 className="mb-2 text-xs font-medium uppercase tracking-wider text-ink-faint">Notes</h4>
          <p className="whitespace-pre-wrap text-sm text-ink">{lead.notes}</p>
        </section>
      )}
    </div>
  );
}

function LeadConversation({ leadId, messages }: { leadId: string; messages: Message[] | undefined }) {
  const startChat = useStartAdminChat();
  const endChat = useEndAdminChat();
  const sendMessage = useSendAdminMessage();
  const toast = useToast();
  const [body, setBody] = useState("");

  useEffect(() => {
    if (!messages) return;
    const last = document.getElementById("conversation-bottom");
    last?.scrollIntoView({ block: "end" });
  }, [messages?.length]);

  const onSend = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = body.trim();
    if (!trimmed) return;
    try {
      await sendMessage.mutateAsync({ leadId, body: trimmed });
      setBody("");
    } catch (err) {
      toast.error("Failed to send message", err instanceof Error ? err.message : undefined);
    }
  };

  return (
    <div className="flex h-full flex-col gap-3">
      <div className="flex items-center justify-end gap-2">
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              onClick={() => startChat.mutate(leadId)}
              disabled={startChat.isPending}
            >
              Take over chat
            </Button>
          </TooltipTrigger>
          <TooltipContent>Pauses the bot for this lead and routes replies to you.</TooltipContent>
        </Tooltip>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => endChat.mutate(leadId)}
          disabled={endChat.isPending}
        >
          Hand back
        </Button>
      </div>

      <div className="flex-1 overflow-y-auto rounded-md border border-line bg-surface-muted/40 p-3 scrollbar-thin">
        {!messages ? (
          <Skeleton className="h-32 w-full" />
        ) : messages.length === 0 ? (
          <p className="px-2 py-8 text-center text-sm text-ink-muted">No messages yet.</p>
        ) : (
          <ol className="space-y-2.5">
            {messages.map((m) => (
              <ConversationBubble key={m.id} message={m} />
            ))}
          </ol>
        )}
        <div id="conversation-bottom" />
      </div>

      <form onSubmit={onSend} className="space-y-2">
        <Textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder="Write a message as admin..."
          rows={3}
          onKeyDown={(e) => {
            if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
              e.preventDefault();
              void onSend(e as unknown as React.FormEvent);
            }
          }}
        />
        <div className="flex items-center justify-between">
          <p className="text-xs text-ink-faint">⌘/Ctrl + Enter to send</p>
          <Button type="submit" variant="primary" size="md" disabled={sendMessage.isPending || !body.trim()}>
            <Send className="h-4 w-4" />
            Send
          </Button>
        </div>
      </form>
    </div>
  );
}

function ConversationBubble({ message }: { message: Message }) {
  const isUser = message.role === "user";
  const isAdmin = message.source === "admin";
  return (
    <li className={`flex ${isUser ? "justify-end" : "justify-start"}`}>
      <div
        className={`max-w-[82%] rounded-md px-3 py-2 text-sm leading-relaxed whitespace-pre-wrap ${
          isUser
            ? "bg-accent-soft text-ink"
            : isAdmin
              ? "bg-info/15 text-ink"
              : "border border-line bg-surface text-ink"
        }`}
      >
        <div className="mb-1 flex items-center gap-2 text-xs text-ink-faint">
          <span className="uppercase tracking-wider">
            {isAdmin ? "Admin" : message.role}
          </span>
          <span>·</span>
          <span>{formatRelative(message.created_at)}</span>
        </div>
        {message.body}
      </div>
    </li>
  );
}
